import {
  err,
  ok,
  parseInstancePreferences,
  preferencesWriteBody,
  PREFERENCES_PATH,
  type AbortLike,
  type AuthScope,
  type InstancePreferences,
  type PreferencesPatch,
  type PreferencesWriteProblem,
  type Result,
} from '@visious/core';
import type { Platform } from '../platform/instance';
import { authGet, authWrite, type AuthApiProblem } from './auth';

/**
 * `GET` / `POST /api/v1/auth/preferences` — §8's preference sync.
 *
 * The whole endpoint fits in two functions, and the second one makes **two** requests on purpose.
 * That is not an oversight to optimise away later: a `POST` here replaces the entire document, so a
 * write is only expressible as a merge over a fresh read (API reference §8d, finding 1). Caching the
 * read and merging into that would mean a push resetting whatever changed on the account since — from
 * another tab, from Invidious's own web UI, or from a phone — back to what this tab last saw.
 */

/** A write this build refused to send because the endpoint would have silently discarded it. */
export interface PreferencesWriteRefusal {
  readonly kind: 'unwritable';
  readonly problems: readonly PreferencesWriteProblem[];
}

export type PreferencesProblem = AuthApiProblem | PreferencesWriteRefusal;

export async function fetchPreferences(
  platform: Platform,
  grantedScopes: readonly AuthScope[] | null,
  signal?: AbortLike,
): Promise<Result<InstancePreferences, PreferencesProblem>> {
  const raw = await authGet(platform, PREFERENCES_PATH, grantedScopes, signal);
  if (!raw.ok) return err(raw.error);

  const parsed = parseInstancePreferences(raw.value);
  // R4's boundary: a document that is not a document is a parse problem the page can name, not a throw.
  return parsed.ok ? ok(parsed.value) : err({ kind: 'parse', error: parsed.error });
}

/**
 * Push a patch: read the document, merge, validate, send.
 *
 * It answers with the preferences **as they were just written** rather than with `null`, and that is
 * worth the line: finding 5 says a `204` cannot be told from "nothing happened", so the merged document
 * is the closest thing to a receipt that exists without a third request. The caller stores it as the
 * new baseline for the next diff.
 *
 * There is deliberately **no verifying re-read**. The two silent-discard causes are a wrong-typed field
 * and a body the server cannot parse; `preferencesWriteBody` refuses the first before the wire and this
 * layer only ever sends `JSON.stringify` of an object for the second. What a re-read would catch is a
 * concurrent write from another client — which is a race no third request closes either.
 */
export async function pushPreferences(
  platform: Platform,
  patch: PreferencesPatch,
  grantedScopes: readonly AuthScope[] | null,
  signal?: AbortLike,
): Promise<Result<InstancePreferences, PreferencesProblem>> {
  const current = await fetchPreferences(platform, grantedScopes, signal);
  if (!current.ok) return err(current.error);

  const body = preferencesWriteBody(current.value, patch);
  if (!body.ok) return err({ kind: 'unwritable', problems: body.error });

  const written = await authWrite(platform, 'POST', PREFERENCES_PATH, body.value, grantedScopes, signal);
  if (!written.ok) return err(written.error);

  // The body that went out *is* the new document — the endpoint stores what it is told (finding 4), and
  // parsing our own body back gives the caller the same shape a fresh `GET` would.
  const parsed = parseInstancePreferences(body.value);
  return parsed.ok ? ok(parsed.value) : err({ kind: 'parse', error: parsed.error });
}
